import { useState } from 'react';
import {
    CssBaseline,
    Box,
    Toolbar,
    Typography,
    Divider,
    IconButton,
    List,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import TeacherSideBar from './TeacherSideBar';
import { Navigate, Route, Routes } from 'react-router-dom';
import Logout from '../Logout';
import AccountMenu from '../../components/AccountMenu';
import { AppBar, Drawer } from '../../components/styles';
import StudentAttendance from '../admin/studentRelated/StudentAttendance';
import TeacherClassDetails from './TeacherClassDetails';
import TeacherComplain from './TeacherComplain';
import TeacherHomePage from './TeacherHomePage';
import TeacherProfile from './TeacherProfile';
import TeacherViewStudent from './TeacherViewStudent';
import StudentExamMarks from '../admin/studentRelated/StudentExamMarks';
import AddNote from './AddNote';

const primaryTeal = '#004d40';
const accentGold = '#ffcc00';
const hoverTeal = '#006666';
const darkTeal = '#00251a';

const TeacherDashboard = () => {
    const [open, setOpen] = useState(true);

    const toggleDrawer = () => {
        setOpen(!open);
    };

    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar 
                    open={open} 
                    position="absolute"
                    sx={{
                        backgroundColor: primaryTeal,
                        boxShadow: '0 4px 20px rgba(0, 77, 64, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 6px 25px rgba(0, 77, 64, 0.5)',
                        },
                    }}
                >
                    <Toolbar sx={{ pr: '24px' }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && { display: 'none' }),
                                color: accentGold,
                                '&:hover': {
                                    backgroundColor: hoverTeal,
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{ 
                                flexGrow: 1,
                                fontWeight: 'bold',
                                letterSpacing: '1px',
                                color: accentGold,
                            }}
                        >
                            Teacher Dashboard
                        </Typography>
                        <AccountMenu />
                    </Toolbar>
                </AppBar>
                <Drawer
                    variant="permanent"
                    open={open}
                    sx={{
                        display: open ? 'block' : 'none',
                        width: open ? 240 : 0,
                        transition: 'width 0.3s ease',
                        '& .MuiDrawer-paper': {
                            backgroundColor: darkTeal,
                            color: '#ffffff',
                            borderRight: `2px solid ${accentGold}`,
                            boxShadow: '0 4px 20px rgba(0, 77, 64, 0.3)',
                        },
                    }}
                >
                    <Toolbar sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end',
                        backgroundColor: primaryTeal,
                    }}>
                        <IconButton 
                            onClick={toggleDrawer}
                            sx={{ color: accentGold }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <Divider sx={{ backgroundColor: accentGold }} />
                    <List component="nav">
                        <TeacherSideBar />
                    </List>
                </Drawer>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        padding: { xs: '16px', sm: '24px' },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <Toolbar />
                    <Routes>
                        <Route path="/" element={<TeacherHomePage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                        <Route path="/teacher/dashboard" element={<TeacherHomePage />} />
                        <Route path="/teacher/profile" element={<TeacherProfile />} />
                        <Route path="/teacher/complain" element={<TeacherComplain />} />
                        <Route path="/teacher/class" element={<TeacherClassDetails />} />
                        <Route path="/teacher/class/student/:id" element={<TeacherViewStudent />} />
                        <Route
                            path="/teacher/class/student/attendance/:studentID/:subjectID"
                            element={<StudentAttendance situation="Subject" />}
                        />
                        <Route
                            path="/teacher/class/student/marks/:studentID/:subjectID"
                            element={<StudentExamMarks situation="Subject" />}
                        />
                        <Route path="/teacher/add-note" element={<AddNote />} />
                        <Route path="/logout" element={<Logout />} />
                    </Routes>
                </Box>
            </Box>
        </>
    );
};

export default TeacherDashboard;
